import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import ScienceBackground from '../../components/layout/ScienceBackground';
import { FaMapMarkerAlt, FaClock, FaCalendarAlt, FaChevronRight, FaFilter, FaLaptop, FaBuilding } from 'react-icons/fa';
import API_BASE_URL from '../../config/api';

const TimeTables = () => {
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, physical, online

  useEffect(() => {
    fetchInstitutes();
  }, []);

  const fetchInstitutes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/get_institutes.php`);
      const data = await response.json();
      if (data.status === 'success') {
        setInstitutes(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch time tables. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filteredInstitutes = filter === 'all' 
    ? institutes 
    : institutes.filter(inst => inst.type === filter);

  return (
    <div className="font-sans bg-[#f8fafc] min-h-screen text-slate-800 overflow-x-hidden selection:bg-orange-500 selection:text-white relative">
      <ScienceBackground />
      <Navbar />

      {/* Hero Section - Modern Minimalist */}
      <div className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-end justify-between gap-8"
          >
            <div className="max-w-3xl">
              <span className="inline-block px-4 py-1.5 bg-orange-100 text-orange-600 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
                Class Schedules
              </span>
              <h1 className="text-5xl md:text-7xl font-black text-slate-900 leading-tight">
                Plan Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-red-500 to-rose-600">Learning</span> Journey
              </h1>
              <p className="text-xl text-slate-500 mt-6 leading-relaxed">
                Explore our comprehensive class timetables across multiple institutes. 
                Synchronize your education with our flexible physical and digital sessions.
              </p>
            </div>

            {/* Filter Pills */}
            <div className="flex bg-white p-1.5 rounded-xl shadow-xl shadow-slate-200/50 border border-slate-100 self-start md:self-auto">
              {['all', 'physical', 'online'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilter(type)}
                  className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all capitalize ${
                    filter === type 
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg shadow-orange-200' 
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-6 pb-32 relative z-10">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 border-4 border-orange-100 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="mt-6 text-slate-400 font-medium animate-pulse">Synchronizing schedules...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center text-3xl mb-6">!</div>
            <h3 className="text-2xl font-bold text-slate-800 mb-2">Connection Error</h3>
            <p className="text-slate-500">{error}</p>
          </div>
        ) : (
          <div className="space-y-16">
            <AnimatePresence mode='popLayout'>
              {filteredInstitutes.map((institute, idx) => (
                <motion.section
                  key={institute.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className="relative"
                >
                  {/* Institute Branding Row */}
                  <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar Info Card */}
                    <div className="lg:w-1/4">
                      <div className="bg-white rounded-2xl p-6 shadow-xl shadow-slate-200/50 border border-slate-50 sticky top-32">
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center text-white text-3xl font-black mb-6 shadow-lg shadow-orange-100">
                          {institute.name.charAt(0)}
                        </div>
                        <h2 className="text-3xl font-black text-slate-900 mb-4">{institute.name}</h2>
                        
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <div className="mt-1 p-2 bg-slate-50 rounded-lg text-orange-500">
                              <FaMapMarkerAlt size={14} />
                            </div>
                            <div>
                              <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Location</p>
                              <p className="text-slate-700 font-semibold">{institute.location}</p>
                            </div>
                          </div>

                          <div className="flex items-start gap-3">
                            <div className="mt-1 p-2 bg-slate-50 rounded-lg text-orange-500">
                              {institute.type === 'online' ? <FaLaptop size={14} /> : <FaBuilding size={14} />}
                            </div>
                            <div>
                              <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Session Type</p>
                              <p className="text-slate-700 font-semibold capitalize">{institute.type} Class</p>
                            </div>
                          </div>
                        </div>

                        <button className="w-full mt-8 py-3 bg-slate-900 hover:bg-black text-white rounded-lg font-bold transition-all flex items-center justify-center gap-2 group">
                          View Details
                          <FaChevronRight className="text-xs group-hover:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>

                    {/* Schedule Grid - Modern Table */}
                    <div className="lg:w-3/4">
                      <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-white shadow-xl overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr className="bg-slate-900/5">
                                <th className="px-6 py-5 text-xs font-black text-slate-400 uppercase tracking-widest">Code</th>
                                <th className="px-6 py-5 text-xs font-black text-slate-400 uppercase tracking-widest">Session / Segment</th>
                                <th className="px-6 py-5 text-xs font-black text-slate-400 uppercase tracking-widest">Day</th>
                                <th className="px-6 py-5 text-xs font-black text-slate-400 uppercase tracking-widest text-right">Time Window</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100/50">
                              {institute.schedules && institute.schedules.length > 0 ? (
                                institute.schedules.map((schedule, sIdx) => (
                                  <motion.tr
                                    key={schedule.id}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.1 + (sIdx * 0.05) }}
                                    className="hover:bg-white/80 transition-colors group"
                                  >
                                    <td className="px-6 py-6">
                                      <span className="font-mono text-sm font-black text-orange-500 bg-orange-50 px-3 py-1 rounded-lg">
                                        {schedule.session_code || '#CLASS'}
                                      </span>
                                    </td>
                                    <td className="px-6 py-6">
                                      <p className="font-bold text-slate-800 group-hover:text-orange-600 transition-colors leading-tight">
                                        {schedule.class_segment}
                                      </p>
                                    </td>
                                    <td className="px-6 py-6">
                                      <div className="flex items-center gap-2 text-slate-500">
                                        <FaCalendarAlt size={12} className="text-slate-300" />
                                        <span className="text-sm font-bold">{schedule.class_day}</span>
                                      </div>
                                    </td>
                                    <td className="px-6 py-6 text-right">
                                      <div className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-1.5 rounded-xl text-sm font-bold shadow-lg shadow-slate-200">
                                        <FaClock size={12} className="text-orange-400" />
                                        {schedule.time_window}
                                      </div>
                                    </td>
                                  </motion.tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="px-6 py-20 text-center text-slate-400 italic">
                                    <div className="flex flex-col items-center justify-center opacity-50">
                                      <FaClock size={32} className="mb-4" />
                                      <p>No active sessions scheduled yet.</p>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.section>
              ))}
            </AnimatePresence>

            {filteredInstitutes.length === 0 && !loading && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }}
                className="py-32 text-center"
              >
                <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
                  <FaFilter size={32} />
                </div>
                <h3 className="text-2xl font-bold text-slate-400">No matching schedules found</h3>
                <button 
                  onClick={() => setFilter('all')}
                  className="mt-4 text-orange-600 font-bold hover:underline"
                >
                  Clear all filters
                </button>
              </motion.div>
            )}
          </div>
        )}
      </div>

      {/* Decorative Floating Elements */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-0 overflow-hidden opacity-40">
        <div className="absolute top-[10%] right-[-5%] w-96 h-96 bg-orange-200 rounded-full blur-[120px] mix-blend-multiply animate-pulse"></div>
        <div className="absolute bottom-[20%] left-[-5%] w-[30rem] h-[30rem] bg-rose-100 rounded-full blur-[120px] mix-blend-multiply animate-blob"></div>
      </div>

      <Footer />
    </div>
  );
};

export default TimeTables;

